import { FormInput, RadioGroup, RadioGroupDataInterface } from '@systemeio/ui'
import { useAddFunnelStep } from 'modules/funnels/funnel/hooks/use-add-funnel-step'
import { useUpdateFunnelStepQuery } from 'modules/funnels/funnel/hooks/use-update-funnel-step-query'
import {
  CreateFunnelStepErrorsInterface,
  CreateFunnelStepInterface,
} from 'modules/funnels/funnel/types/funnel-step-interface'
import { getFunnelStepPageTypeDataOptions } from 'modules/funnels/funnel/utils/get-funnel-step-page-type-data-options'
import { FunnelId } from 'modules/funnels/types/funnel-interface'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import CreateModal from 'shared/components/create-modal'
import { FormSelectWithGroups } from 'shared/components/form-select/form-select-with-groups'
import {
  FunnelGroupEnum,
  FunnelStepSourceEnum,
  funnelGroupTitleLocoKeys,
  funnelStepSourceLocoKeys,
} from 'shared/enums/funnel-step-type-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { fetchWithFieldErrors } from 'shared/utils/fetch-with-errors'
import { useFunnelStepsList } from '../hooks/use-funnel-steps-list'
import { AddIcon } from '@systemeio/ui/icons'

const defaultValues: CreateFunnelStepInterface = {
  name: '',
  type: undefined,
  funnelStepSource: FunnelStepSourceEnum.ChooseTemplate,
}

const defaultErrors: CreateFunnelStepErrorsInterface = {
  fields: {
    name: '',
    type: '',
  },
}

function CreateFunnelStepModal() {
  const router = useRouter()
  const { t } = useLocoTranslation()
  const funnelId = parseInt(router.query.funnelId as string) as FunnelId
  const { mutateFunnelStepsList } = useFunnelStepsList(funnelId)
  const [errors, setErrors] = useState(defaultErrors)
  const [opened, setOpened] = useState(false)

  const { updateFunnelStepQuery } = useUpdateFunnelStepQuery()
  const { addFunnelStep } = useAddFunnelStep()
  const onClose = () => setOpened(false)

  const afterLeave = () => {
    setErrors(defaultErrors)
  }

  const handleCreate = async (data: CreateFunnelStepInterface) => {
    await fetchWithFieldErrors(async () => {
      const responseData = await addFunnelStep(Number(funnelId), data)
      await mutateFunnelStepsList(data => {
        if (data) {
          return [...data, responseData]
        }
      }, false)
      updateFunnelStepQuery(responseData.id)
      onClose()
    }, setErrors)
  }

  const funnelStepTypeGroupsData = Object.values(FunnelGroupEnum).map(key => {
    return {
      id: key,
      caption: t(funnelGroupTitleLocoKeys[key]),
    }
  })

  const funnelStepSourceData: RadioGroupDataInterface<FunnelStepSourceEnum>[] = [
    {
      id: FunnelStepSourceEnum.ChooseTemplate,
      caption: t(funnelStepSourceLocoKeys[FunnelStepSourceEnum.ChooseTemplate]),
    },
    {
      id: FunnelStepSourceEnum.StartFromScratch,
      caption: t(funnelStepSourceLocoKeys[FunnelStepSourceEnum.StartFromScratch]),
    },
  ]

  return (
    <>
      <div
        className={
          'border-[rgb(var(--text-primary))]/20 cursor-pointer border-b-[1px] px-[15px] py-[15px] shadow-lg ring-1 ring-black/5 last:border-b-transparent'
        }
        onClick={() => setOpened(true)}
        data-test-element={'create-funnel-step-button'}
      >
        <div className="flex items-center justify-center gap-[10px]">
          <AddIcon className={`fill-brand-primary`} />
          <span className={'text-brand-primary text-lg'}>
            {t('dashboard.funnel.create_funnel_step')}
          </span>
        </div>
      </div>
      <CreateModal
        opened={opened}
        onClose={onClose}
        afterLeave={afterLeave}
        defaultValues={defaultValues}
        caption={t('dashboard.funnel.funnel_step')}
        saveDataAttribute={'create-funnel-step-save-button'}
        creatable={[
          {
            field: 'name',
            onRender: (value, onChange) => (
              <FormInput
                key="funnel-step-name"
                label={t('global.name')}
                value={value}
                onChange={e => onChange(e.target.value)}
                error={errors.fields.name}
                data-test-element={'create-funnel-step-name-field'}
                required
              />
            ),
          },
          {
            field: 'type',
            onRender: (value, onChange) => (
              <FormSelectWithGroups
                key="funnel-step-type"
                className={'sm:min-w-0'}
                data={getFunnelStepPageTypeDataOptions(t)}
                label={t('global.type')}
                value={value}
                onChange={value => onChange(value)}
                placeholder={t('dashboard.funnel.funnel_step_type_placeholder')}
                error={errors.fields.type}
                dataTest={{ ['data-test-element']: 'create-funnel-step-type-field' }}
                groups={funnelStepTypeGroupsData}
                required
                withoutCloseIcon
              />
            ),
          },
          {
            field: 'funnelStepSource',
            onRender: (value, onChange) => (
              <RadioGroup
                className={'items-center'}
                selected={value}
                options={funnelStepSourceData}
                onChange={value => onChange(value)}
              />
            ),
          },
        ]}
        onCreate={handleCreate}
        requiredFields={['name', 'type']}
      />
    </>
  )
}

export default CreateFunnelStepModal
